<template>
  <div class="content-wrapper">
    <section class="content-header">
      <h1>
        Mood
      </h1>
      <ol class="breadcrumb">
        <li>
          <router-link :to="{ name: 'dashboard' }">
            <i class="fa fa-dashboard"></i> Home
          </router-link>
        </li>
        <li>
          <router-link :to="{name: 'mood-index'}">
            Moods
          </router-link>
        </li>
        <li class="active">
          <span v-if="isCreate">Create</span>
          <span v-else>Edit</span>
        </li>
      </ol>
    </section>

    <section class="content">
      <div class="row">
        <div class="col-md-12">
          <div class="box box-info">
            <div class="box-header with-border">
              <h3 class="box-title">
                {{ title }}
              </h3>
            </div>

            <form class="form-horizontal" @submit.prevent="submit">
              <div class="box-body">
                <div v-if="error" class="alert alert-danger">
                  <a class="close" @click="error = false">
                    &times;
                  </a>
                  {{ error }}
                </div>

                <div class="form-group">
                  <label for="name"
                    class="col-sm-2 control-label">
                    Name
                  </label>

                  <div class="col-sm-10">
                    <input
                      type="text"
                      class="form-control"
                      id="name"
                      placeholder="Name"
                      v-model="form.name">
                  </div>
                </div>

                <div class="form-group">
                  <label for="slug"
                    class="col-sm-2 control-label">
                    Slug
                  </label>

                  <div class="col-sm-10">
                    <input
                      type="text"
                      class="form-control"
                      id="slug"
                      placeholder="Slug"
                      v-model="form.slug">
                  </div>
                </div>

                <div class="form-group">
                  <label for="parent" class="col-sm-2 control-label">
                    Image
                  </label>

                  <div class="col-sm-10">
                    <div class="row">
                      <div class="col-md-2">
                        <img v-if="form.image.url"
                          class="img-thumbnail img-responsive"
                          alt="Destination Image"
                          :src="parseImage(form.image.url, '_thumb')">

                        <img v-else
                          class="img-thumbnail img-responsive"
                          src="/static/img/img_placeholder.png"
                          alt="No image">
                      </div>

                      <div class="col-md-10">
                        <button class="btn btn-sm btn-secondary"
                          @click.prevent="showModal('gallery')">
                          Select Image
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <transition name="slideDown">
                  <modal title="Select Mood Image"
                    v-if="show.gallery">
                    <FileGallery
                      type="mood"
                      @update-images="setImage"
                      :selected="form.image" />
                  </modal>
                </transition>
              </div>

              <div class="box-footer">
                <button type="submit" class="btn btn-info pull-right" v-if="isCreate">Create</button>
                <button type="submit" class="btn btn-info pull-right" v-else>Update</button>
                <a class="btn btn-default" @click.prevent="cancel">Cancel</a>
              </div>

            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import bus from '@/utils/bus';
import Common from '@/mixins/Common';
import Modal from '@/components/modules/common/Modal';
import FileGallery from '@/components/modules/common/FileGallery';

export default {
  name: 'MoodForm',

  mixins: [Common],

  data() {
    return {
      title: 'Add new Mood',

      form: {
        id: null,
        name: null,
        slug: null,
        image: {
          id: null,
          url: null,
        },
      },

      show: {
        gallery: false,
      },

      error: null,
    };
  },

  props: {
    isCreate: {
      type: Boolean,
      required: false,
    },
  },

  components: {
    FileGallery,
    Modal,
  },

  methods: {
    async fetch() {
      this.axios.get(`/moods/${this.$route.params.id}`)
        .then((response) => {
          const { data } = response.data;

          this.form.id = data.id;
          this.form.name = data.name;
          this.form.slug = data.slug;
          this.form.image.id = data.image_id;
          this.form.image.url = data.image_url;
        })
        .catch((error) => {
          if (error.response) {
            this.error = error.response.data.errors;
          }
        });
    },

    validate() {
      if (!this.form.name) {
        this.$swal('Name required', 'Please fill name to continue', 'warning');
        return false;
      }

      if (!this.form.slug) {
        this.$swal('Slug required', 'Please fill slug to continue', 'warning');
        return false;
      }

      if (!this.form.image.id || !this.form.image.url) {
        this.$swal('Image required', 'Please select image to continue', 'warning');
        return false;
      }

      return true;
    },

    submit() {
      const result = this.validate();
      if (!result) return;

      if (this.isCreate) {
        this.create();
      } else {
        this.update();
      }
    },

    create() {
      const data = {
        name: this.form.name,
        slug: this.form.slug,
        image: this.form.image.id,
      };

      this.axios.post('/moods', this.stringify(data))
        .then(() => {
          this.$router.push({ name: 'mood-index' });
        })
        .catch((error) => {
          if (error.response) {
            this.error = error.response.data.errors;
          }

          if (error) {
            this.$swal({
              title: 'Create Failed!',
              text: 'Failed to create new Mood',
              type: 'warning',
              timer: 3000,
            });
          }
        });
    },

    update() {
      const data = {
        id: this.form.id,
        name: this.form.name,
        slug: this.form.slug,
        image: this.form.image.id,
      };

      this.axios.put(`/moods/${this.form.id}`, this.stringify(data))
        .then(() => {
          this.$router.push({ name: 'mood-index' });
        })
        .catch((error) => {
          if (error.response) {
            this.error = error.response.data.errors;
          }

          if (error) {
            this.$swal({
              title: 'Update Failed!',
              text: `Failed to update ${this.form.name}`,
              type: 'warning',
              timer: 3000,
            });
          }
        });
    },

    showModal(modal) {
      this.$store.commit('SET_MODAL', modal);
      this.show[modal] = true;
      document.body.classList.add('locked');
    },

    setImage(payload) {
      this.form.image = payload.images;
    },

    cancel() {
      this.$router.go(-1);
    },
  },

  created() {
    if (!this.isCreate) {
      this.fetch();
      this.title = 'Edit Mood';
    }
  },

  mounted() {
    bus.$on('close-modal', () => {
      this.show.gallery = false;
    });
  },

  destroyed() {
    bus.$off('close-modal');
  },
};
</script>



// WEBPACK FOOTER //
// src/components/modules/moods/Form.vue